import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress,
  Alert, Stepper, Step, StepLabel, TextField, Divider, Radio, RadioGroup,
  FormControlLabel, FormControl, FormLabel, Chip,
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import GavelIcon from '@mui/icons-material/Gavel';
import EditIcon from '@mui/icons-material/Edit';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  expressInterest, createInvestmentFlow, signInvestment, fundInvestment,
  createPaymentIntent, getWireInstructions,
} from '../services/api';

const STEPS = ['Express Interest', 'Review Terms', 'Sign Documents', 'Fund Investment'];

const InvestmentFlow = () => {
  const { startupId } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('wire');
  const [investmentId, setInvestmentId] = useState(null);
  const [wireInfo, setWireInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);

  const handleExpressInterest = async () => {
    if (!amount || Number(amount) <= 0) { setError('Please enter a valid amount'); return; }
    setLoading(true); setError(null);
    try {
      const res = await expressInterest({ startupId, amount: Number(amount) });
      setInvestmentId(res.data?.investmentId || res.data?.id);
      setActiveStep(1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to express interest');
    } finally { setLoading(false); }
  };

  const handleReviewTerms = async () => {
    setLoading(true); setError(null);
    try {
      const res = await createInvestmentFlow({ startupId, amount: Number(amount), investmentId });
      setInvestmentId(res.data?.investmentId || res.data?.id || investmentId);
      setActiveStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to proceed');
    } finally { setLoading(false); }
  };

  const handleSign = async () => {
    setLoading(true); setError(null);
    try {
      await signInvestment(investmentId, { agreed: true });
      setActiveStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to sign documents');
    } finally { setLoading(false); }
  };

  const handleFund = async () => {
    setLoading(true); setError(null);
    try {
      if (paymentMethod === 'wire') {
        const wireRes = await getWireInstructions(investmentId);
        setWireInfo(wireRes.data);
      } else {
        await createPaymentIntent({ investmentId, amount: Number(amount), method: 'stripe' });
      }
      await fundInvestment(investmentId, { method: paymentMethod, amount: Number(amount) });
      setCompleted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Payment failed');
    } finally { setLoading(false); }
  };

  if (completed) {
    return (
      <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <Box sx={{ background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)', color: 'white', py: 6 }}>
          <Container maxWidth="lg">
            <Typography variant="h3" sx={{ fontWeight: 700 }}>Investment Complete</Typography>
          </Container>
        </Box>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Card sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Thank You!</Typography>
            <Typography color="textSecondary" sx={{ mt: 1, mb: 3 }}>
              Your investment of ${Number(amount).toLocaleString()} has been submitted successfully.
            </Typography>
            {wireInfo && (
              <Box sx={{ backgroundColor: '#f5f5f5', p: 3, borderRadius: 2, mx: 4, textAlign: 'left' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Wire Transfer Instructions</Typography>
                {Object.entries(wireInfo).map(([k, v]) => (
                  <Typography key={k} variant="body2">
                    <strong>{k.replace(/([A-Z])/g, ' $1').trim()}:</strong> {v}
                  </Typography>
                ))}
              </Box>
            )}
            <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ mt: 3, backgroundColor: '#478289' }}>
              Back to Dashboard
            </Button>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700 }}>Invest</Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, mt: 1 }}>Complete your investment in a few steps</Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        <Card>
          <CardContent sx={{ p: 4 }}>
            {/* Step 0: Express Interest */}
            {activeStep === 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <MonetizationOnIcon sx={{ color: '#478289', fontSize: 32 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>Express Interest</Typography>
                </Box>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                  Enter the amount you'd like to invest. Minimum investment applies per deal terms.
                </Typography>
                <TextField
                  fullWidth label="Investment Amount (USD)" type="number" value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                  sx={{ mb: 3 }}
                />
                <Button variant="contained" size="large" onClick={handleExpressInterest}
                  disabled={loading} sx={{ backgroundColor: '#478289' }}>
                  {loading ? <CircularProgress size={24} /> : 'Express Interest'}
                </Button>
              </Box>
            )}

            {/* Step 1: Review Terms */}
            {activeStep === 1 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <GavelIcon sx={{ color: '#478289', fontSize: 32 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>Review Terms</Typography>
                </Box>
                <Box sx={{ backgroundColor: '#f5f5f5', p: 3, borderRadius: 2, mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>Investment Amount:</strong> ${Number(amount).toLocaleString()}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                    By proceeding, you acknowledge that: (1) This is a private placement offering.
                    (2) Investments in startups carry high risk and may result in total loss.
                    (3) These securities are illiquid and cannot be easily sold.
                    (4) You meet the accredited investor requirements.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button onClick={() => setActiveStep(0)}>Back</Button>
                  <Button variant="contained" onClick={handleReviewTerms} disabled={loading}
                    sx={{ backgroundColor: '#478289' }}>
                    {loading ? <CircularProgress size={24} /> : 'Accept & Continue'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Step 2: Sign Documents */}
            {activeStep === 2 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <EditIcon sx={{ color: '#478289', fontSize: 32 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>Sign Documents</Typography>
                </Box>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                  Review and electronically sign the subscription agreement and related documents.
                </Typography>
                <Box sx={{ backgroundColor: '#f5f5f5', p: 3, borderRadius: 2, mb: 3 }}>
                  <Typography variant="body2">
                    By clicking "Sign", you agree to the subscription agreement, operating agreement,
                    and all related investment documents. This constitutes your electronic signature.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button onClick={() => setActiveStep(1)}>Back</Button>
                  <Button variant="contained" onClick={handleSign} disabled={loading}
                    sx={{ backgroundColor: '#478289' }}>
                    {loading ? <CircularProgress size={24} /> : 'Sign Documents'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Step 3: Fund */}
            {activeStep === 3 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <PaymentIcon sx={{ color: '#478289', fontSize: 32 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>Fund Your Investment</Typography>
                </Box>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                  Choose your payment method to complete the investment.
                </Typography>
                <FormControl sx={{ mb: 3 }}>
                  <FormLabel>Payment Method</FormLabel>
                  <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                    <FormControlLabel value="wire" control={<Radio />} label="Wire Transfer" />
                    <FormControlLabel value="stripe" control={<Radio />} label="Credit Card (Stripe)" />
                  </RadioGroup>
                </FormControl>
                <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Amount:</strong> ${Number(amount).toLocaleString()} USD
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button onClick={() => setActiveStep(2)}>Back</Button>
                  <Button variant="contained" onClick={handleFund} disabled={loading}
                    sx={{ backgroundColor: '#478289' }}>
                    {loading ? <CircularProgress size={24} /> : 'Complete Payment'}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default InvestmentFlow;
